import { NavLink, useLocation } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/App";
import {
  LayoutDashboard, Users, MapPin, Store, Package, ShoppingCart, Wallet,
  Gift, ClipboardList, Receipt, Bell, ChevronLeft, ChevronRight,
  UserCog, Navigation, CalendarDays, Route, Radar, ChevronDown, ChevronUp,
  Target, ScrollText, Eye, Trophy, CheckCircle, Ruler, BarChart3,
  MessageSquare, Globe, FileText, Briefcase, Brain, Settings,
  BookOpen, Shield, Award, Smartphone, TrendingUp, GitBranch,
} from "lucide-react";
import { PravaahLogo } from "@/components/PravaahLogo";

// Admin roles that should NOT see the salesman-only "My Performance" link
// (the leaderboard is sufficient for them; My Performance is per-user only).
const ADMIN_ROLES = new Set([
  "SUPER_ADMIN", "SALES_ADMIN", "FACTORY_ADMIN", "ACCOUNTS_ADMIN", "AREA_MANAGER",
]);

const Sidebar = ({ collapsed, setCollapsed }) => {
  const location = useLocation();
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.has(user?.role || "");

  const groups = [
    {
      id: "field-force",
      label: "Field Force",
      icon: MapPin,
      basePath: "/field-force",
      matchPaths: ["/field-force", "/field-tracking", "/field-checkins"],
      items: [
        { icon: Radar, label: "FF Dashboard", path: "/field-force/dashboard", testId: "sidebar-ff-dashboard" },
        { icon: UserCog, label: "Punch In/Out", path: "/field-checkins", testId: "sidebar-field-checkins" },
        { icon: Navigation, label: "Live Tracking", path: "/field-tracking", testId: "sidebar-field-tracking" },
        { icon: Eye, label: "360° View", path: "/field-force/360", testId: "sidebar-ff-360" },
        { icon: Route, label: "Routes & BEATs", path: "/field-force/beats", testId: "sidebar-ff-beats" },
      ],
    },
    {
      id: "sales",
      label: "Sales",
      icon: Briefcase,
      basePath: "/sales-group",
      matchPaths: ["/dealers", "/products", "/orders", "/collections", "/schemes", "/scheme-claims", "/brochures", "/onboarding"],
      items: [
        { icon: UserCog, label: "Onboarding", path: "/onboarding", testId: "sidebar-onboarding" },
        { icon: Store, label: "Dealers", path: "/dealers", testId: "sidebar-dealers" },
        { icon: Package, label: "Products", path: "/products", testId: "sidebar-products" },
        { icon: ShoppingCart, label: "Orders", path: "/orders", testId: "sidebar-orders" },
        { icon: Wallet, label: "Collections", path: "/collections", testId: "sidebar-collections" },
        { icon: Gift, label: "Schemes", path: "/schemes", testId: "sidebar-schemes" },
        { icon: Award, label: "Scheme Claims", path: "/scheme-claims", testId: "sidebar-scheme-claims" },
        { icon: BookOpen, label: "Brochures", path: "/brochures", testId: "sidebar-brochures" },
      ],
    },
    {
      id: "operations",
      label: "Operations",
      icon: Settings,
      basePath: "/ops-group",
      matchPaths: ["/employees", "/targets", "/tasks", "/expenses", "/hr/", "/audit", "/inventory"],
      items: [
        { icon: Users, label: "Employees", path: "/employees", testId: "sidebar-employees" },
        { icon: Target, label: "Targets", path: "/targets", testId: "sidebar-targets" },
        { icon: ClipboardList, label: "Tasks", path: "/tasks", testId: "sidebar-tasks" },
        { icon: Receipt, label: "Expenses", path: "/expenses", testId: "sidebar-expenses" },
        { icon: CalendarDays, label: "Attendance", path: "/hr/attendance", testId: "sidebar-hr-attendance" },
        { icon: ClipboardList, label: "Leave", path: "/hr/leave", testId: "sidebar-hr-leave" },
        { icon: Package, label: "Inventory", path: "/inventory", testId: "sidebar-inventory" },
        { icon: ScrollText, label: "Audit Trail", path: "/audit", testId: "sidebar-audit" },
      ],
    },
    {
      id: "intelligence",
      label: "Intelligence",
      icon: Brain,
      basePath: "/intel-group",
      matchPaths: ["/leaderboard", "/dealer-map", "/scheme-achievements", "/reports", "/sales-forecast", "/analytics", "/analytics/cost-per-bag", "/sales-analytics", "/report-scheduling"],
      items: [
        { icon: BarChart3, label: "Analytics", path: "/analytics", testId: "sidebar-analytics" },
        { icon: TrendingUp, label: "Sales Analytics", path: "/sales-analytics", testId: "sidebar-sales-analytics" },
        ...(isAdmin ? [{ icon: Wallet, label: "Cost-per-Bag", path: "/analytics/cost-per-bag", testId: "sidebar-cost-per-bag" }] : []),
        { icon: CalendarDays, label: "Report Scheduling", path: "/report-scheduling", testId: "sidebar-report-scheduling" },
        { icon: Trophy, label: "Leaderboard", path: "/leaderboard", testId: "sidebar-leaderboard" },
        ...(!isAdmin ? [{ icon: Trophy, label: "My Performance", path: "/my-performance", testId: "sidebar-my-performance" }] : []),
        { icon: Ruler, label: "Dealer Map", path: "/dealer-map", testId: "sidebar-dealer-map" },
        { icon: BarChart3, label: "Scheme Progress", path: "/scheme-achievements", testId: "sidebar-scheme-achievements" },
        { icon: ScrollText, label: "Reports", path: "/reports", testId: "sidebar-reports" },
        { icon: Target, label: "Sales Forecast", path: "/sales-forecast", testId: "sidebar-sales-forecast" },
      ],
    },
    {
      id: "comms",
      label: "Communication",
      icon: MessageSquare,
      basePath: "/comms-group",
      matchPaths: ["/chat", "/webhooks", "/grievances"],
      items: [
        { icon: MessageSquare, label: "Team Chat", path: "/chat", testId: "sidebar-chat" },
        { icon: Globe, label: "Webhooks", path: "/webhooks", testId: "sidebar-webhooks" },
        { icon: ScrollText, label: "Grievances", path: "/grievances", testId: "sidebar-grievances" },
      ],
    },
    {
      id: "system",
      label: "System",
      icon: Settings,
      basePath: "/sys-group",
      matchPaths: ["/api-docs", "/notifications", "/push-notifications", "/users", "/loyalty", "/flows", "/flow-insights", "/flow-marketplace", "/custom-triggers", "/mobile-integration-docs"],
      items: [
        { icon: GitBranch, label: "Flow Builder", path: "/flows", testId: "sidebar-flows" },
        { icon: Smartphone, label: "Mobile SDK Guide", path: "/mobile-integration-docs", testId: "sidebar-mobile-docs" },
        { icon: FileText, label: "API Docs", path: "/api-docs", testId: "sidebar-api-docs" },
        { icon: Bell, label: "Notifications", path: "/notifications", testId: "sidebar-notifications" },
        { icon: Smartphone, label: "Push (FCM)", path: "/push-notifications", testId: "sidebar-push" },
        { icon: UserCog, label: "User Management", path: "/users", testId: "sidebar-users" },
        { icon: Gift, label: "Loyalty & Gifts", path: "/loyalty", testId: "sidebar-loyalty" },
      ],
    },
  ];

  const isGroupActive = (group) => {
    if (location.pathname.startsWith(group.basePath)) return true;
    if (group.matchPaths) {
      return group.matchPaths.some((p) => location.pathname.startsWith(p));
    }
    return false;
  };

  const initialExpanded = {};
  groups.forEach((g) => {
    initialExpanded[g.id] = isGroupActive(g);
  });
  const [expanded, setExpanded] = useState(initialExpanded);

  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const renderNavItem = (item) => {
    const isActive = location.pathname === item.path;
    return (
      <NavLink
        key={item.path}
        to={item.path}
        className={`flex items-center gap-3 px-3 py-1.5 mx-1 rounded-md text-[13px] transition-colors ${
          isActive
            ? "bg-[#8B1A1A] text-white font-medium"
            : "text-slate-400 hover:text-white hover:bg-white/5"
        }`}
        data-testid={item.testId}
        title={collapsed ? item.label : ""}
      >
        <item.icon className="w-3.5 h-3.5 shrink-0" />
        {!collapsed && <span className="truncate">{item.label}</span>}
      </NavLink>
    );
  };

  const renderGroup = (group) => {
    const active = isGroupActive(group);
    const isOpen = expanded[group.id];
    const Icon = group.icon;

    if (collapsed) {
      return (
        <div key={group.id} className="relative group">
          <NavLink
            to={group.items[0]?.path || "#"}
            className={`flex items-center justify-center py-2 mx-2 rounded-lg ${
              active ? "bg-[#8B1A1A]/20 text-[#8B1A1A]" : "text-slate-400 hover:text-white hover:bg-white/5"
            }`}
            title={group.label}
            data-testid={`sidebar-group-${group.id}`}
          >
            <Icon className="w-4 h-4" />
          </NavLink>
        </div>
      );
    }

    return (
      <div key={group.id} className="mt-0.5">
        <button
          onClick={() => toggle(group.id)}
          className={`flex items-center justify-between w-full px-3 py-2 mx-1 rounded-md text-[13px] transition-colors ${
            active ? "text-white" : "text-slate-500 hover:text-white hover:bg-white/5"
          }`}
          style={{ width: "calc(100% - 8px)" }}
          data-testid={`sidebar-group-${group.id}`}
        >
          <div className="flex items-center gap-3">
            <Icon className="w-4 h-4 shrink-0" />
            <span className="font-medium tracking-wide text-xs uppercase">{group.label}</span>
          </div>
          {isOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
        {isOpen && (
          <div className="ml-3 border-l border-slate-700/40 pl-1 space-y-0.5 pb-1">
            {group.items.map(renderNavItem)}
          </div>
        )}
      </div>
    );
  };

  return (
    <aside
      className={`${collapsed ? "w-16" : "w-60"} bg-[#1A1A2E] min-h-screen flex flex-col transition-all duration-200 shrink-0`}
      data-testid="sidebar"
    >
      {/* Header */}
      <div className="p-4 flex items-center justify-between border-b border-slate-700/50">
        {!collapsed ? (
          <PravaahLogo variant="full" size={32} dataTestId="sidebar-logo" />
        ) : (
          <PravaahLogo variant="badge" size={32} dataTestId="sidebar-logo-collapsed" />
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="text-slate-500 hover:text-white p-1 rounded"
          data-testid="toggle-sidebar"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-2 overflow-y-auto space-y-0.5">
        {/* Dashboard - standalone */}
        <NavLink
          to="/dashboard"
          className={`flex items-center gap-3 px-3 py-2 mx-2 rounded-lg text-sm transition-colors ${
            location.pathname === "/dashboard"
              ? "bg-[#8B1A1A] text-white font-medium"
              : "text-slate-400 hover:text-white hover:bg-white/5"
          }`}
          data-testid="sidebar-dashboard"
          title={collapsed ? "Dashboard" : ""}
        >
          <LayoutDashboard className="w-4 h-4 shrink-0" />
          {!collapsed && <span>Dashboard</span>}
        </NavLink>

        {/* Admin Panel - standalone */}
        <NavLink
          to="/admin"
          className={`flex items-center gap-3 px-3 py-2 mx-2 rounded-lg text-sm transition-colors ${
            location.pathname === "/admin"
              ? "bg-[#8B1A1A] text-white font-medium"
              : "text-slate-400 hover:text-white hover:bg-white/5"
          }`}
          data-testid="sidebar-admin"
          title={collapsed ? "Admin" : ""}
        >
          <Shield className="w-4 h-4 shrink-0" />
          {!collapsed && <span>Admin</span>}
        </NavLink>

        {/* Groups */}
        {groups.map(renderGroup)}
      </nav>
    </aside>
  );
};

export default Sidebar;
